import { ChevronLeft, Pencil, StarIcon } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Link } from "@/components/link";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard.tsx";

interface BrandHeaderProps {
  readonly brandName: string;
  readonly isNew?: boolean;
  readonly onEditIcon?: () => void;
  readonly profilePicture?: string;
}

export function BrandHeader({
  brandName,
  isNew,
  onEditIcon,
  profilePicture,
}: BrandHeaderProps) {
  return (
    <div className="flex items-center gap-lg rounded-t-xl bg-bg-primary px-lg pb-lg pt-xl sm:px-2xl">
      <Link className="hover:no-underline" href="/brands">
        <Button
          className="rounded-xl"
          color="color"
          isIconOnly
          size="sm"
          variant="secondary"
        >
          <AccessibleIcon>
            <ChevronLeft size={18} />
          </AccessibleIcon>
        </Button>
      </Link>

      {onEditIcon ? (
        <button
          aria-label={`Edit ${brandName} icon`}
          className="group/avatar-btn relative size-10 shrink-0 rounded-lg overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onEditIcon}
          type="button"
        >
          <Avatar
            alt={brandName}
            className="size-10 rounded-lg"
            objectFit="contain"
            src={profilePicture}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover/avatar-btn:opacity-100">
            <Pencil className="text-white" size={16} />
          </div>
        </button>
      ) : (
        <Avatar
          alt={brandName}
          className="size-10 rounded-lg shrink-0"
          objectFit="contain"
          src={profilePicture}
        />
      )}

      <div className="flex items-center gap-2xl">
        <span className="text-2xl font-semibold text-text-primary">
          {brandName}
        </span>
        {isNew && (
          <FeatureGuard feature="brand.new-badge.view">
            <Badge isRounded variant="blue">
              <StarIcon className="mr-1" size={14} />
              New
            </Badge>
          </FeatureGuard>
        )}
      </div>
    </div>
  );
}
